import { errorView, type ErrorView } from '@krp/client-runtime'

/**
 * Итог последней команды окна — для экрана, не данные сервера: «отправляется»,
 * «отказ с текстом сервера», «ответ не получен». Срок жизни — `screen`.
 *
 * `unknown` — изменяющий запрос ушёл без ответа (ADR-11): повторять запрещено,
 * экран проверяет состояние ЧТЕНИЕМ.
 */
export type CommandPhase = 'sending' | 'rejected' | 'unknown' | 'done'

export interface CommandFeedback {
  /** Какая команда: `session.open`, `shift.close`… */
  command: string
  /** Цель команды: место или касса — чтобы отказ показать у нужной строки. */
  target: string
  phase: CommandPhase
  error: ErrorView | null
  /** Подпись итога для экрана (готово/проверено). */
  note: string | null
}

/**
 * Коды ответа 503, которыми служба говорит «команда НЕ выполнена»: до эффекта не прошла проверка прав. В отличие от 502/504 шлюза
 * (`gateway-timeout`: служба могла успеть) здесь исход известен — оплата не начиналась, повтор безопасен, ждать минуты не нужно.
 */
const REFUSED_BEFORE_EFFECT: ReadonlySet<string> = new Set(['access-unavailable', 'access-proof-rejected'])

export function refusedBeforeEffect(error: unknown): boolean {
  const view = errorView(error)
  return view.kind === 'unavailable' && REFUSED_BEFORE_EFFECT.has(view.code)
}

/**
 * Исход команды неизвестен: ответа нет (`unknown-outcome`), ответ не прочитан
 * (`invalid-response`) ИЛИ пришёл 5xx. 5xx на
 * изменяющий запрос — это и отказ службы до эффекта (503 кабинета), и таймаут
 * Gateway/прокси уже после того, как служба команду исполнила (502/504): по
 * ответу их не различить, поэтому команда считается «могла выполниться» и
 * проверяется ЧТЕНИЕМ. Лишняя проверка стоит один GET, ошибочный повтор — второй
 * эффект на кассе.
 */
export function isOutcomeUnknown(error: unknown): boolean {
  const view = errorView(error)
  // Служба сама назвала причину отказа ДО эффекта (проверка прав недоступна): команда не выполнялась, замка «исход неизвестен» нет.
  if (refusedBeforeEffect(error)) return false
  // Ответ пришёл, но не прошёл декодер (2xx другой схемы): команда, возможно, исполнена.
  if (view.kind === 'unknown-outcome' || view.kind === 'invalid-response') return true
  const status = typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined
  return typeof status === 'number' && status >= 500
}

export function feedbackOfError(command: string, target: string, error: unknown): CommandFeedback {
  return {
    command,
    target,
    phase: isOutcomeUnknown(error) ? 'unknown' : 'rejected',
    error: errorView(error),
    note: null,
  }
}
